import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { PackingOperation, PackingTask, PackingListItem } from '../schema';
import { queryItemTypesWithLocations } from '../schema';
import { useMemo } from 'react';
import { differenceInDays } from 'date-fns';

interface ValidationIssue {
    type: 'incomplete_tasks' | 'unconfirmed_items' | 'ended_events';
    count?: number;
    tasks?: PackingTask[];
    items?: Array<{ eventName: string; item: PackingListItem }>;
    events?: Array<{ event: { id: string; name: string; endDate: string | null }; daysAgo: number; itemCount: number }>;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    operation: PackingOperation;
}

export default function CompleteOperationDialog({ open, onClose, onConfirm, operation }: Props) {
    const { data: itemTypesData } = queryItemTypesWithLocations();

    const allItemLocations = useMemo(() => {
        const locations: Array<{
            itemTypeId: string;
            quantity: number;
            eventId: string | null;
            storageLocationId: string | null;
        }> = [];
        const itemTypes = itemTypesData?.itemTypes || [];
        for (const itemType of itemTypes) {
            const itemLocations =
                (itemType as { itemLocations?: Array<{ event?: { id: string }; storageLocation?: { id: string }; quantity?: number }> })
                    .itemLocations || [];
            for (const location of itemLocations) {
                if ((location.quantity || 0) > 0) {
                    locations.push({
                        itemTypeId: itemType.id,
                        quantity: location.quantity || 0,
                        eventId: location.event?.id || null,
                        storageLocationId: location.storageLocation?.id || null,
                    });
                }
            }
        }
        return locations;
    }, [itemTypesData]);

    // Validation logic
    const validationIssues = useMemo((): ValidationIssue[] => {
        const issues: ValidationIssue[] = [];
        const now = new Date();

        // Check 1: Incomplete tasks
        const tasks = (operation.packingTasks || []) as PackingTask[];
        const incompleteTasks = tasks.filter(t => !t.completed);
        if (incompleteTasks.length > 0) {
            issues.push({
                type: 'incomplete_tasks',
                count: incompleteTasks.length,
                tasks: incompleteTasks,
            });
        }

        // Check 2: Unconfirmed items
        const targetEvents = (operation.targetEvents || []) as PackingOperation['targetEvents'][number][];
        const unconfirmedItems: Array<{ eventName: string; item: PackingListItem }> = [];
        for (const event of targetEvents) {
            const packingListItems = (event.packingListItems || []) as PackingListItem[];
            for (const item of packingListItems) {
                if (!item.confirmed) {
                    unconfirmedItems.push({
                        eventName: event.name,
                        item,
                    });
                }
            }
        }
        if (unconfirmedItems.length > 0) {
            issues.push({
                type: 'unconfirmed_items',
                count: unconfirmedItems.length,
                items: unconfirmedItems,
            });
        }

        // Check 3: Items at ended events
        const sourceEvents = (operation.sourceEvents || []) as PackingOperation['sourceEvents'][number][];
        const endedEventsWithItems: Array<{
            event: { id: string; name: string; endDate: string | null };
            daysAgo: number;
            itemCount: number;
        }> = [];

        for (const event of sourceEvents) {
            if (event.endDate) {
                const endDate = new Date(event.endDate);
                if (endDate < now) {
                    const itemsAtEvent = allItemLocations.filter(loc => loc.eventId === event.id);
                    const totalItemCount = itemsAtEvent.reduce((sum, loc) => sum + loc.quantity, 0);

                    if (totalItemCount > 0) {
                        const daysAgo = differenceInDays(now, endDate);
                        endedEventsWithItems.push({
                            event: {
                                id: event.id,
                                name: event.name,
                                endDate: event.endDate
                                    ? typeof event.endDate === 'string'
                                        ? event.endDate
                                        : new Date(event.endDate).toISOString()
                                    : null,
                            },
                            daysAgo,
                            itemCount: totalItemCount,
                        });
                    }
                }
            }
        }

        if (endedEventsWithItems.length > 0) {
            issues.push({
                type: 'ended_events',
                events: endedEventsWithItems,
            });
        }

        return issues;
    }, [operation, allItemLocations]);

    const hasWarnings = validationIssues.length > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Complete Packing Operation?</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    {/* Soft warnings summary: operation can be completed but issues were found */}
                    {hasWarnings && (
                        <Typography variant="body2" color="text.secondary">
                            You can complete this operation. Please review the following:
                        </Typography>
                    )}

                    {/* Warning Sections */}
                    {validationIssues.map((issue, idx) => {
                        if (issue.type === 'incomplete_tasks') {
                            return (
                                <Alert key={idx} severity="warning" icon={<WarningIcon />}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {issue.count} task{issue.count !== 1 ? 's are' : ' is'} still incomplete
                                    </Typography>
                                    {issue.tasks && issue.tasks.length > 0 && (
                                        <List dense sx={{ mt: 1, pl: 2 }}>
                                            {issue.tasks.slice(0, 5).map(task => (
                                                <ListItem key={task.id} disablePadding>
                                                    <ListItemText
                                                        primary={`${task.itemType?.name || 'Unknown'} - ${task.quantity} items`}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                            {issue.tasks.length > 5 && (
                                                <ListItem disablePadding>
                                                    <ListItemText
                                                        primary={`... and ${issue.tasks.length - 5} more`}
                                                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                                    />
                                                </ListItem>
                                            )}
                                        </List>
                                    )}
                                </Alert>
                            );
                        }

                        if (issue.type === 'unconfirmed_items') {
                            return (
                                <Alert key={idx} severity="warning" icon={<WarningIcon />}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {issue.count} item{issue.count !== 1 ? "s haven't" : " hasn't"} been confirmed
                                    </Typography>
                                    {issue.items && issue.items.length > 0 && (
                                        <List dense sx={{ mt: 1, pl: 2 }}>
                                            {issue.items.slice(0, 5).map((itemData, itemIdx) => (
                                                <ListItem key={itemIdx} disablePadding>
                                                    <ListItemText
                                                        primary={`${itemData.item.itemType?.name || 'Unknown'} at ${itemData.eventName}`}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                            {issue.items.length > 5 && (
                                                <ListItem disablePadding>
                                                    <ListItemText
                                                        primary={`... and ${issue.items.length - 5} more`}
                                                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                                    />
                                                </ListItem>
                                            )}
                                        </List>
                                    )}
                                </Alert>
                            );
                        }

                        if (issue.type === 'ended_events') {
                            return (
                                <Alert key={idx} severity="error" icon={<ErrorIcon />}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {issue.events?.length} event{issue.events?.length !== 1 ? 's have' : ' has'} ended but still have
                                        items
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Items may be left behind
                                    </Typography>
                                    {issue.events && issue.events.length > 0 && (
                                        <List dense sx={{ mt: 1, pl: 2 }}>
                                            {issue.events.map((eventData, eventIdx) => (
                                                <ListItem key={eventIdx} disablePadding>
                                                    <ListItemText
                                                        primary={`${eventData.event.name} ended ${eventData.daysAgo} day${eventData.daysAgo !== 1 ? 's' : ''} ago, has ${eventData.itemCount} item${eventData.itemCount !== 1 ? 's' : ''}`}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </Alert>
                            );
                        }

                        return null;
                    })}

                    {/* Confirmation Text */}
                    <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                            This operation cannot be undone
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Marking this operation as complete will:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                            <Typography component="li" variant="body2" color="text.secondary">
                                Move operation to history (read-only)
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                                Allow creation of new packing operations
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                                Lock all task checkboxes
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onConfirm} variant="contained" color={hasWarnings ? 'warning' : 'primary'}>
                    {hasWarnings ? 'Complete Anyway' : 'Complete'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
